import { type PostHogConfig } from "posthog-js";

import { dropGenericSafariFetchExceptions } from "./dropGenericSafariFetchExceptions";
import { consumePostHogCrossDomainBootstrap } from "./posthogCrossDomainBootstrap";
import { dropUnactionablePostHogExceptions } from "./shouldDropPostHogException";

/**
 * `advanced_disable_feature_flags_on_first_load` is honoured by posthog-js but
 * is missing from the published config type. It skips the `/flags` request
 * that runs before any organisation group exists. That unscoped response is
 * false for organisation flags and was hiding Checklists and Diary.
 */
export type PostHogClientOptions = Partial<PostHogConfig> & {
  advanced_disable_feature_flags_on_first_load?: boolean;
};

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

const crossDomainBootstrap =
  typeof window !== "undefined" ? consumePostHogCrossDomainBootstrap() : null;

export function getPostHogClientOptions(): PostHogClientOptions {
  return {
    api_host: POSTHOG_HOST ?? "",
    defaults: "2026-01-30",
    /** Share one first-party distinct ID across www and app after explicit consent. */
    cross_subdomain_cookie: true,
    cookieWinsOnConflict: true,
    ...(crossDomainBootstrap ? { bootstrap: crossDomainBootstrap } : {}),
    cookieless_mode: "on_reject",
    opt_out_persistence_by_default: true,
    /**
     * Organisation flags are false until the organisation group is attached.
     * Defer the first `/flags` request until PostHogOrgGroupSync reloads them.
     */
    advanced_disable_feature_flags_on_first_load: true,
    /** DOM autocapture is marketing-site only; app uses explicit capture calls. */
    autocapture: false,
    /** Avoid loading dead-clicks and web-vitals extension bundles on first paint. */
    capture_dead_clicks: false,
    capture_performance: false,
    __preview_deferred_init_extensions: true,
    /**
     * Browser exception autocapture feeds PostHog Error Tracking (a self-driving
     * signal source). Events go through the normal capture pipeline, so consent
     * opt-out still applies.
     */
    capture_exceptions: true,
    /**
     * Drop unactionable native fetch noise before Error Tracking:
     * Clerk session-touch heartbeats (ASA-1506) and empty-stack Safari/WebKit
     * TypeError "Load failed" / "Failed to fetch" (ASA-1507).
     */
    before_send: (event) =>
      dropGenericSafariFetchExceptions(dropUnactionablePostHogExceptions(event)),
  } as PostHogClientOptions;
}

export function getPostHogPublicConfig(): { apiKey: string; host: string } | null {
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    return null;
  }
  return { apiKey: POSTHOG_KEY, host: POSTHOG_HOST };
}

export function warnIfPostHogEnvMissing(isEnabled: boolean): void {
  if (!isEnabled || (POSTHOG_KEY && POSTHOG_HOST)) {
    return;
  }
  const missing: string[] = [];
  if (!POSTHOG_KEY) missing.push("VITE_PUBLIC_POSTHOG_KEY");
  if (!POSTHOG_HOST) missing.push("VITE_PUBLIC_POSTHOG_HOST");
  console.error(
    `[App] PostHog environment variables are not set (${missing.join(", ")}). Add them to your production env for analytics.`,
  );
}
